import type { ApiError } from '../api/admin';
import { IconAlert, IconCloudOff, IconSearch } from './Icons';

/**
 * Shown when an admin request fails.
 *
 * Deliberately tighter than the equivalent on the customer and partner sites: a control
 * centre is read all day by people who already know the system, so it states what went
 * wrong and offers the one useful action rather than explaining itself at length.
 *
 * Retry appears only when retrying could plausibly work. A 403 is not a transient
 * condition, and a button that cannot help is worse than no button.
 *
 * None of these is an empty state: a request that failed must never be rendered as
 * "no records found".
 */
export function ErrorState({
  error,
  message,
  title,
  onRetry,
}: {
  error?: ApiError;
  message?: string;
  title?: string;
  onRetry?: () => void;
}) {
  const kind = error?.kind ?? 'unknown';
  const text = message ?? error?.message ?? 'Something went wrong.';

  const heading =
    title ??
    {
      network: 'Unable to connect',
      timeout: 'That took too long',
      auth: 'Your session has expired',
      forbidden: 'You do not have access',
      notFound: 'Not found',
      conflict: 'That could not be applied',
      server: 'The API had a problem',
      validation: 'Check the details',
      unknown: 'Something went wrong',
    }[kind];

  const canRetry = onRetry && (error === undefined || error.retryable);

  return (
    <div className="admin-state" role="alert">
      <span className="admin-state__icon" aria-hidden="true">
        {kind === 'network' || kind === 'timeout' ? <IconCloudOff /> : <IconAlert />}
      </span>
      <div className="admin-state__body">
        <h2 className="admin-state__title">{heading}</h2>
        <p className="admin-state__text">{text}</p>
        {error?.status !== undefined && (
          <p className="admin-state__meta">HTTP {error.status}</p>
        )}
      </div>
      {canRetry && (
        <button type="button" className="btn btn-sm btn-primary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Shown when a request succeeded and returned nothing.
 *
 * Separate from ErrorState on purpose: conflating the two is how an outage ends up looking
 * like a quiet day, and an administrator stops investigating.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="admin-state admin-state--empty">
      <span className="admin-state__icon" aria-hidden="true">
        <IconSearch />
      </span>
      <div className="admin-state__body">
        <h2 className="admin-state__title">{title}</h2>
        {description && <p className="admin-state__text">{description}</p>}
      </div>
      {action}
    </div>
  );
}
